import React, { Fragment } from "react";
import PropTypes from "prop-types";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Formik, Form, FieldArray } from "formik";
import { object, string, array } from "yup";
import { uniqueId } from "lodash";

import AdvancedSearchCriteria, {
  convertDateFormat,
  OSD_SEARCH_FIELD,
} from "src/components/AdvancedSearch/AdvancedSearchCriteria";
import parseInitialValue from "src/utils/parseAdvancedSearchInitialValue";

const styles = {
  card: {
    position: "absolute",
    width: "100%",
    top: 8,
    left: 0,
    zIndex: 3,
    borderColor: theme => theme.palette.grey[400],
  },
  andText: {
    marginBottom: 2,
    color: theme => theme.palette.primary.main,
    fontWeight: 500,
    textAlign: "center",
  },
  cardActions: {
    padding: 2,
    justifyContent: "space-between",
  },
  cancelButton: {
    textTransform: "none",
    marginLeft: 1,
  },
  searchButton: {
    textTransform: "none",
    marginLeft: 1,
  },
};

const validationSchema = object().shape({
  criteria: array().of(
    object().shape({
      field: string().required("Field is required"),
      text: string().required("Text is required"),
      matchType: string().required("Match type is required"),
    })
  ),
});

const AdvancedSearch = ({ onCancel, onSearch, initialValue, className, fields }) => {
  const initialValues = {
    criteria: parseInitialValue(initialValue),
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => {
        // Only convert timestamp fields (not expiration/manufacture dates which are strings)
        const timestampFields = ['created_at', 'updated_at', 'last_in', 'last_out'];
        
        const searchString = values.criteria
          .map(criterion => {
            // Convert date format only for timestamp fields
            const searchValue = timestampFields.includes(criterion.field)
              ? convertDateFormat(criterion.text)
              : criterion.text;
            
            const matchType = criterion.field === OSD_SEARCH_FIELD ? "=" : criterion.matchType;
            return `${criterion.field} ${matchType} ${searchValue}`;
          })
          .join(" and ");
        onSearch(searchString);
        onCancel();
      }}
      validationSchema={validationSchema}
    >
      {({ values }) => (
        <Form>
          <FieldArray name="criteria">
            {({ push, remove }) => (
              <ClickAwayListener onClickAway={onCancel}>
                <Card variant="outlined" className={className} sx={styles.card}>
                  <CardContent>
                    {values.criteria.map((criterion, index) => (
                      <Fragment key={criterion.id}>
                        {index > 0 && <Typography sx={styles.andText}>AND</Typography>}
                        <AdvancedSearchCriteria
                          index={index}
                          removeCriterion={remove}
                          fields={fields}
                        />
                      </Fragment>
                    ))}
                  </CardContent>
                  <CardActions sx={styles.cardActions}>
                    <Button
                      color="primary"
                      onClick={() =>
                        push({
                          id: uniqueId("criteria_"),
                          field: "",
                          text: "",
                          matchType: "~",
                        })
                      }
                    >
                      Add Criteria
                    </Button>
                    <Box>
                      <Button sx={styles.cancelButton} variant="outlined" onClick={onCancel}>
                        Cancel
                      </Button>
                      <Button
                        sx={styles.searchButton}
                        color="primary"
                        variant="contained"
                        type="submit"
                      >
                        Search
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </ClickAwayListener>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
};

AdvancedSearch.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
  className: PropTypes.string,
  fields: PropTypes.array,
};

export default AdvancedSearch;
